import { computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAppContext } from '@/composables/useAppContext';
import { useItemsGrid } from '@/composables/useItemsGrid';
import { useItemTotals } from '@/composables/useItemTotals';
import { useCategorySummary } from '@/composables/useCategorySummary';
import { useFilterContextRefresh } from '@rdrive/data-filters';

export default () => {
  const store = useStore();
  const { t } = useI18n();
  const dataLoaded = computed(() => store.getters.getDataLoaded);

  const app = useAppContext();
  const grid = useItemsGrid({ ...app, filterContext: app.filterItemsContext, t });

  // --- summary cards (D2b) -------------------------------------------------
  const showOpenItemsOnly = app.showOpenItemsOnly;

  const {
    totalItems,
    totalSubItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    filterByMyTeams,
    filterByMe,
    setAssignedToMyTeams,
    setAssignedToMe,
    refresh: refreshTotals
  } = useItemTotals({
    conn: app.conn,
    filterContext: app.filterItemsContext,
    userId: app.userId,
    showOpenItemsOnly: app.showOpenItemsOnly
  });

  useFilterContextRefresh(app.filterItemsContext, app.dataLoaded, refreshTotals,
    { debounce: 100, immediate: true, requireDataLoaded: true });

  const handleCardFilterChange = async ({ id, selected }) => {
    if (id === 'totalAssignedToYourTeams') {
      await setAssignedToMyTeams(selected);
    } else if (id === 'totalAssignedToYou') {
      await setAssignedToMe(selected);
    }
  };

  // The full-reload cycle (R4): App.vue watches loadDashboard and re-runs
  // getData(), which re-initialises the filter contexts; the FAB then re-applies
  // its filters. The three commits and their order are unchanged.
  const reloadWithOpenItemsOnly = (value) => {
    store.commit('setShowOpenItemsOnly', value);
    store.commit('setShowWelcomeDialog', true);
    store.commit('setLoadDashboard', true);
  };

  // --- project summary (D2a) -----------------------------------------------
  const {
    loading: summaryLoading,
    categories,
    subcategories,
    statusCounts,
    reload: reloadSummary
  } = useCategorySummary({
    conn: app.conn,
    apiHost: app.apiHost,
    projectCode: app.projectCode
  });

  // No initial load: the first paint comes from App's filter-init tick. This
  // quirk is deliberate — see R2, do not "fix" it here.
  useFilterContextRefresh(app.filterItemsContext, app.dataLoaded, reloadSummary,
    { immediate: false, requireDataLoaded: false });

  onMounted(async () => {
    await store.commit('resetFilters');
  });

  return {
    dataLoaded,
    grid,
    showOpenItemsOnly,
    totalItems,
    totalSubItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    filterByMyTeams,
    filterByMe,
    handleCardFilterChange,
    reloadWithOpenItemsOnly,
    summaryLoading,
    categories,
    subcategories,
    statusCounts
  };
};
